import React from 'react';
import PropTypes from 'prop-types';
import { Text, Spinner } from '@zillow/constellation';
import { AdStateProvider, AD_SLOTS } from '@zillow/ad-state-machine';
import { MstSentry } from '@zillow/mst-sentry';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { sentryScope } from '../../../util/tracking';

import {
    getHasAdGptScriptLoaded,
    getHasAdPrebidScriptFailed,
    getHasAdPrebidScriptLoaded,
    getIsMobileLayout,
    getNumListColumns,
} from '../util/getFromState';
import { getAdAbTrialMapping, getAdTargetDiv, isIABSupported } from '../util/common';
import { SearchStateAd } from './SearchStateAd';

const StyledLoadingText = styled(Text)`
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    position: absolute;
    top: 0;
    width: 100%;
`;

const StyledAdWrapper = styled.div`
    ${({ isIABAdStyle, isTall }) =>
        isIABAdStyle &&
        isTall &&
        css`
            position: absolute;
            top: 15px;
        `};
`;

const StyledIFrameWrapper = styled.div`
    border-radius: 4px;
    box-shadow: rgba(0, 0, 0, 0.3) 0px 2px 4px 0px;
    height: 100%;
    position: relative;
    ${({ isIABAd, isTall, numListColumns }) =>
        isIABAd &&
        css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: ${isTall && numListColumns === 1 ? '287px' : '100%'};
        `};
    &:after {
        ${({ isIABAd, isTall }) =>
            isIABAd &&
            isTall &&
            css`
                content: 'Advertisement';
                font-color: #2a2a33;
                font-weight: 400;
                line-height: 10px;
                font-size: 10px;
                position: absolute;
                bottom: 5px;
                margin: auto;
                text-align: center;
            `};
    }
`;

const AdStateParentContainerCmp = ({
    adSlot,
    hideLoadingSpinner,
    onSlotRenderEndedSlot,
    isTall,
    isIABAd,
    hasBeenVisible,
}) => {
    const numListColumns = useSelector(getNumListColumns);
    const isMobile = useSelector(getIsMobileLayout);
    const adGptScriptLoaded = useSelector(getHasAdGptScriptLoaded);
    const adPrebidScriptFailed = useSelector(getHasAdPrebidScriptFailed);
    const adPrebidScriptLoaded = useSelector(getHasAdPrebidScriptLoaded);
    const iabEnabled = isIABSupported({ numListColumns, isTall, isMobile });
    const targetDiv = getAdTargetDiv(adSlot);

    const childAd = (
        <AdStateProvider abTrialMapping={getAdAbTrialMapping()}>
            <SearchStateAd
                adSlot={adSlot}
                targetDiv={targetDiv}
                onSlotRenderEndedSlot={onSlotRenderEndedSlot}
                iabEnabled={iabEnabled}
                adGptScriptLoaded={adGptScriptLoaded}
                adPrebidScriptFailed={adPrebidScriptFailed && !adPrebidScriptLoaded}
                adPrebidScriptLoaded={adPrebidScriptLoaded}
                isTall={isTall}
                hasBeenVisible={hasBeenVisible}
            />
        </AdStateProvider>
    );

    if (adSlot === AD_SLOTS.DISPLAY) {
        return (
            <div key="generic-display-ad" className="generic-display-ad-container">
                {childAd}
            </div>
        );
    }

    return (
        <>
            <StyledLoadingText
                as="p"
                fontType="bodySmallHeading"
                data-testid="nav-ad-loading-container"
            >
                {!hideLoadingSpinner && <Spinner data-testid="nav-ad-spinner" />}
                {!hideLoadingSpinner ? 'Loading...' : null}
            </StyledLoadingText>
            <StyledIFrameWrapper isIABAd={isIABAd} isTall={isTall} numListColumns={numListColumns}>
                <StyledAdWrapper isIABAdStyle={isIABAd} isTall={isTall}>
                    {childAd}
                </StyledAdWrapper>
            </StyledIFrameWrapper>
        </>
    );
};

AdStateParentContainerCmp.propTypes = {
    adSlot: PropTypes.string.isRequired,
    hideLoadingSpinner: PropTypes.bool,
    onSlotRenderEndedSlot: PropTypes.func,
    isTall: PropTypes.bool,
    isIABAd: PropTypes.bool,
};

export const AdStateParentContainer = React.memo(props => (
    <MstSentry sentryScope={sentryScope}>
        <AdStateParentContainerCmp {...props} />
    </MstSentry>
));
